import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useReenviarVerificacion, useVerificarCorreo } from '../api/hooks'
import { MarcoAcceso } from '../componentes/MarcoAcceso'
import { IconoCheck } from '../componentes/icons'
import { Aviso } from '../componentes/ui'

/**
 * Confirmación de la dirección de correo.
 *
 * Se llega acá desde el enlace del correo, así que el token viene en el query
 * string y la confirmación se dispara sola: pedirle a la persona que además
 * apriete un botón no aporta nada, ya hizo clic en el enlace.
 *
 * Lo que sí importa es que un fallo tenga salida. Un token expirado o ya usado
 * dejaba a la persona en una pantalla muerta; acá se le ofrece pedir otro
 * enlace en el mismo lugar donde se enteró del problema.
 */
export function VerificarCorreo() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const verificar = useVerificarCorreo()
  const reenviar = useReenviarVerificacion()
  const [email, setEmail] = useState('')

  /*
   * El token es de un solo uso: canjearlo dos veces devuelve "ya se usó" y
   * convierte un éxito en un error a la vista de la persona. En desarrollo
   * StrictMode ejecuta los efectos dos veces a propósito, así que sin este
   * candado la pantalla mostraría un fallo en el caso feliz.
   */
  const yaEnviado = useRef(false)

  useEffect(() => {
    if (!token || yaEnviado.current) return
    yaEnviado.current = true
    verificar.mutate({ token })
    // Se depende solo del token a propósito: `verificar` cambia de identidad en
    // cada render, y el candado de arriba es lo que garantiza el único envío.
  }, [token, verificar])

  function pedirOtro(ev: FormEvent) {
    ev.preventDefault()
    if (reenviar.isPending || !email.trim()) return
    reenviar.mutate({ email: email.trim().toLowerCase() })
  }

  const pie = (
    <Link to="/entrar" className="text-[12px] text-acero-500 hover:text-acero-800">
      ← Ir a entrar
    </Link>
  )

  if (verificar.isSuccess) {
    return (
      <MarcoAcceso ancho="max-w-[420px]" pie={pie}>
        <h1 className="text-[17px] font-semibold text-acero-900 flex items-center gap-2">
          <IconoCheck size={18} className="text-ok-500" />
          Correo confirmado
        </h1>
        <div className="mt-3 space-y-3">
          <Aviso tono="ok">{verificar.data.mensaje}</Aviso>
          <Aviso tono="info" titulo="Qué sigue">
            Ya puede entrar. Si su cuenta todavía no tiene rol asignado, un administrador de Compra
            Norte lo hará ahora que la dirección está confirmada.
          </Aviso>
        </div>
        <Link to="/entrar" className="btn btn-accion w-full mt-4">
          Entrar
        </Link>
      </MarcoAcceso>
    )
  }

  if (verificar.isPending) {
    return (
      <MarcoAcceso ancho="max-w-[420px]">
        <h1 className="text-[17px] font-semibold text-acero-900">Confirmando su correo…</h1>
        <p className="text-[12.5px] text-acero-600 mt-1.5 leading-relaxed">
          Un momento. No cierre esta ventana.
        </p>
      </MarcoAcceso>
    )
  }

  // Falta el token en la URL, o el canje falló.
  const motivo = !token
    ? 'El enlace está incompleto: le falta el código de confirmación. Cópielo completo desde el correo, o pida uno nuevo acá abajo.'
    : (verificar.error?.message ?? 'No pudimos confirmar su correo.')

  return (
    <MarcoAcceso ancho="max-w-[420px]" pie={pie}>
      <h1 className="text-[17px] font-semibold text-acero-900">No pudimos confirmar su correo</h1>

      <div className="mt-3">
        <Aviso tono="error">
          <span role="alert">{motivo}</span>
        </Aviso>
      </div>

      {reenviar.isSuccess ? (
        <div className="mt-4">
          <Aviso tono="ok" titulo="Enlace enviado">
            {reenviar.data.mensaje}
          </Aviso>
        </div>
      ) : (
        <form onSubmit={pedirOtro} noValidate className="mt-4 space-y-3">
          <p className="text-[12.5px] text-acero-600 leading-relaxed">
            Escriba su correo y le enviamos un enlace nuevo. Los enlaces vencen a las 24 horas.
          </p>

          {reenviar.error && (
            <Aviso tono="error">
              <span role="alert">{reenviar.error.message}</span>
            </Aviso>
          )}

          <label className="block">
            <span className="etiqueta">Correo</span>
            <input
              className="campo"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={reenviar.isPending}
            />
          </label>

          <button
            type="submit"
            className="btn btn-accion w-full"
            disabled={reenviar.isPending || !email.trim()}
          >
            {reenviar.isPending ? 'Enviando…' : 'Enviarme otro enlace'}
          </button>
        </form>
      )}
    </MarcoAcceso>
  )
}
